import { FC, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Modal, Typography, Table, Badge, cn } from '@crisellevaldez/speculo-ui';
import { STAT_CELL_TEXT } from '../../../utils/statsStyles';
import { CallStatsHeader } from './CallStatsHeader';
import { EditContactModal } from './EditContactModal';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { BackButton } from '../../common/BackButton';
import { EditCallAnalysisModal } from './EditCallAnalysisModal';
import {
  User,
  Voicemail,
  XCircle,
  PhoneIncoming,
  PhoneOutgoing,
  Calendar,
  Phone,
} from 'lucide-react';
import { formatNumber } from '../../../utils/format';
import { CallDetailsPanel } from './CallDetailsPanel';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useOrgFlowOptions } from '../../../hooks/graphql/flows/useOrgFlowOptions';
import { formatDate } from '../../../utils/dateTime';
import { handleDateRangeChange as normalizeDateRange } from '../../../utils/dateRangeTimezone';
import { formatDurationFromSeconds, formatCallDuration } from '../../../utils/duration';
import { formatPhoneNumber, selectCallDisplayNumber } from '../../../utils/formatters';
import { INBOUND_FLOW_OPTION, splitFlowFilterSelection } from '../../../utils/callFlowFilter';
import { AnsweredBy, getAnsweredByStyle, getFlowTypeStyle } from '../../../utils/badgeStyles';
import { OutcomeBadge } from '../../common/OutcomeBadge';
import { Contact } from '../../../types/contact.types';
import type { GQLTransferSummary } from '../../../hooks/graphql/dashboard/useOrgLiveTransfersGQL';
import type { CrmStageProjection } from '../../../types/crmStage.types';
import { CrmStageDisplay } from '../../common/CrmStageDisplay';
import { useShowCrmStage } from '../../../hooks/useShowCrmStage';

export interface GQLCallSummary {
  callId: string;
  contactId?: string;
  contactName?: string;
  toNumber?: string;
  fromNumber?: string;
  inbound?: boolean;
  direction?: string;
  answeredBy?: AnsweredBy;
  initiatedAt?: string;
  durationSeconds?: number;
  recordingUrl?: string;
  flowName?: string;
  flowType?: string;
  status?: string;
  outcome?: string;
  summary?: string;
  transcript?: string;
  callAnalysisTimeline?: string;
  callAnalysisAnswers?: string;
  transfer?: GQLTransferSummary | null;
  crmStages?: CrmStageProjection[];
}

interface Call {
  id: string;
  call_id: string;
  answered_by?: AnsweredBy;
  callTimestamp?: Date;
  corrected_duration?: number;
  contactId?: string;
  isTest?: boolean | null;
  call_analysis?: {
    outcome: string;
    notes?: string;
    timeline?: Array<{ event: string; timestamp: string }>;
    answers?: {
      'Willingness to Speak with an Agent'?: string;
      'Transaction Type'?: string;
      Motivation?: string;
      [key: string]: string | undefined;
    };
    summary?: string;
  };
  call_length?: number;
  concatenated_transcript?: string;
  from_number?: string;
  inbound?: boolean;
  recordingUrl?: string;
  status?: string;
  to_number?: string;
  flow_name?: string;
  flowName?: string;
  flowType?: string;
  contact?: Contact;
  fullName?: string;
  transfer?: GQLTransferSummary | null;
  crmStages?: CrmStageProjection[];
  [key: string]: unknown;
}

interface CallStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  orgTimezone?: string;
  type: 'all' | 'answered' | 'opportunities';
  callsMade: number;
  conversations: number;
  opportunities: number;
  gqlCalls?: GQLCallSummary[];
  gqlTotal?: number;
  gqlHasMore?: boolean;
  gqlLoadMore?: () => void;
  gqlLoadPage?: (offset: number, limit: number) => void;
  gqlSort?: (sortBy: string, sortOrder: string) => void;
  gqlFilter?: (params: {
    search?: string;
    answeredBy?: string;
    flowIds?: string[];
    flowTypes?: string[];
    includeInbound?: boolean;
    dateFrom?: string;
    dateTo?: string;
  }) => void;
  gqlIsLoading?: boolean;
  initialAnsweredBy?: string;
}

// Column key → backend field name for GQL sort
const GQL_SORT_FIELD_MAP: Record<string, string> = {
  callTimestamp: 'initiatedAt',
  corrected_duration: 'durationSeconds',
  answered_by: 'answeredBy',
};

export const CallStatsModal: FC<CallStatsModalProps> = ({
  isOpen,
  onClose,
  dateRange,
  orgTimezone,
  type,
  callsMade,
  conversations,
  opportunities,
  gqlCalls = [],
  gqlTotal,
  gqlLoadPage,
  gqlSort,
  gqlFilter,
  gqlIsLoading,
  initialAnsweredBy,
}) => {
  const GQL_PAGE_SIZE = 25;
  const [gqlCurrentPage, setGqlCurrentPage] = useState(0);
  const [gqlPageSize, setGqlPageSize] = useState(GQL_PAGE_SIZE);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [editOutcomeCallId, setEditOutcomeCallId] = useState<string | null>(null);
  const [showInfoOnMobile, setShowInfoOnMobile] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [contactToEdit, setContactToEdit] = useState<Contact | null>(null);
  const organization = useOrganizationStore((state) => state.organization);
  const showCrmStage = useShowCrmStage();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAnsweredBy, setSelectedAnsweredBy] = useState<string | null>(
    initialAnsweredBy ?? null
  );
  const [selectedFlowIds, setSelectedFlowIds] = useState<string[]>([]);
  const [selectedFlowTypes, setSelectedFlowTypes] = useState<string[]>([]);
  const [modalDateRange, setModalDateRange] = useState<{ from: Date | null; to: Date | null }>(
    dateRange
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch the org's flow id-keyed options lazily — only loaded once the modal opens.
  const {
    options: flowOptions,
    flowTypes: availableFlowTypes,
    isLoading: isFlowOptionsLoading,
  } = useOrgFlowOptions(organization?.id, isOpen);
  const flowTypeOptions = useMemo(
    () => availableFlowTypes.map((t) => ({ value: t, label: t })),
    [availableFlowTypes]
  );
  // Inbound calls have no flow, so "Inbound" is offered as a Flow filter value
  // alongside the org's flows and can be combined with them (SPEC-2383).
  const flowFilterOptions = useMemo(() => [INBOUND_FLOW_OPTION, ...flowOptions], [flowOptions]);

  // Re-sync the modal's date range whenever the parent's dateRange changes
  // (e.g. dashboard date filter), but only while the modal isn't actively
  // overriding it. Compare by timestamp so a new {from,to} object from the
  // parent doesn't clobber the user's modal-side override.
  useEffect(() => {
    setModalDateRange(dateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.from?.getTime(), dateRange.to?.getTime()]);

  // Debounce backend filter. Run on mount too so the parent's persistent
  // useOrgCallsGQL state gets re-synced to this modal session's local filters
  // — otherwise closing the modal mid-filter then reopening would leave the
  // GQL hook scoped to the old filter while the UI shows empty filter chips.
  useEffect(() => {
    if (!gqlFilter) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { flowIds, includeInbound } = splitFlowFilterSelection(selectedFlowIds);
      gqlFilter({
        search: searchQuery || undefined,
        answeredBy: selectedAnsweredBy || undefined,
        flowIds,
        includeInbound,
        flowTypes: selectedFlowTypes.length ? selectedFlowTypes : undefined,
        dateFrom: modalDateRange.from?.toISOString() ?? undefined,
        dateTo: modalDateRange.to?.toISOString() ?? undefined,
      });
      setGqlCurrentPage(0);
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchQuery,
    selectedAnsweredBy,
    selectedFlowIds,
    selectedFlowTypes,
    modalDateRange.from?.getTime(),
    modalDateRange.to?.getTime(),
    gqlFilter,
  ]);

  const handleOpenEditModal = useCallback((contact: Contact) => {
    if (contact) {
      setContactToEdit(contact);
      setShowEditModal(true);
    }
  }, []);

  // Map GQL CallSummary items to the internal Call shape
  const allCalls: Call[] = useMemo(() => {
    return gqlCalls.map((c) => {
      let timeline: { event: string; timestamp: string }[] | undefined;
      let answers: Record<string, string | undefined> | undefined;
      try {
        timeline = c.callAnalysisTimeline ? JSON.parse(c.callAnalysisTimeline) : undefined;
      } catch {
        /* ignore */
      }
      try {
        answers = c.callAnalysisAnswers ? JSON.parse(c.callAnalysisAnswers) : undefined;
      } catch {
        /* ignore */
      }
      const inbound = c.inbound ?? c.direction === 'inbound';
      // Falling back to toNumber labelled every unresolved inbound caller with
      // our own Remi number, so they were indistinguishable (SPEC-2382).
      const displayNumber = selectCallDisplayNumber({
        inbound,
        from_number: c.fromNumber,
        to_number: c.toNumber,
      });
      return {
        id: c.callId,
        call_id: c.callId,
        contactId: c.contactId,
        fullName: c.contactName || formatPhoneNumber(displayNumber),
        inbound,
        from_number: c.fromNumber,
        to_number: c.toNumber,
        answered_by: c.answeredBy,
        callTimestamp: c.initiatedAt
          ? new Date(
              /Z$|[+-]\d{2}:?\d{2}$/.test(c.initiatedAt) ? c.initiatedAt : c.initiatedAt + 'Z'
            )
          : undefined,
        call_length: c.durationSeconds ? c.durationSeconds / 60 : undefined,
        corrected_duration: c.durationSeconds,
        recordingUrl: c.recordingUrl,
        concatenated_transcript: c.transcript,
        flowName: c.flowName || undefined,
        flowType: c.flowType || undefined,
        status: c.status,
        transfer: c.transfer ?? null,
        crmStages: c.crmStages ?? [],
        call_analysis: {
          outcome: c.outcome || '',
          summary: c.summary,
          timeline,
          answers,
        },
      };
    });
  }, [gqlCalls]);

  // Backend handles search/filter — allCalls is already filtered by the server
  const filteredCalls = allCalls;

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    if (gqlSort) {
      setGqlCurrentPage(0);
      gqlSort(GQL_SORT_FIELD_MAP[key] ?? key, direction.toUpperCase());
    }
  };

  const columns = [
    {
      header: 'Name',
      key: 'fullName',
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item: Call) => (
        <div className="flex items-center gap-3 min-w-0">
          {item.inbound ? (
            <PhoneIncoming className="h-3.5 w-3.5 call-color-inbound shrink-0" />
          ) : (
            <PhoneOutgoing className="h-3.5 w-3.5 call-color-outbound shrink-0" />
          )}
          {item.contactId ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal({ id: item.contactId } as Contact);
              }}
              className={`font-medium text-left text-primaryfortext truncate min-w-0 ${item.fullName && item.fullName !== '-' ? 'underline' : ''} cursor-pointer`}
              title={item.fullName || ''}
            >
              {item.fullName || '-'}
            </button>
          ) : (
            <span
              className="font-medium text-left text-muted-foreground truncate min-w-0"
              title={item.fullName || ''}
            >
              {item.fullName || '-'}
            </span>
          )}
          {item.isTest && <Badge className="inline-flex items-center badge-amber">Test</Badge>}
        </div>
      ),
    },
    {
      header: 'Phone',
      key: 'to_number',
      width: '180px',
      cell: (item: Call) => (
        <span className="text-foreground">
          {formatPhoneNumber(selectCallDisplayNumber(item))}
        </span>
      ),
    },
    ...(showCrmStage
      ? [
          {
            header: 'CRM Stage',
            key: 'crmStages',
            width: '220px',
            cell: (item: Call) => <CrmStageDisplay stages={item.crmStages} compact />,
          },
        ]
      : []),
    {
      header: 'Date/Time',
      key: 'callTimestamp',
      sortable: true,
      width: '220px',
      cell: (item: Call) =>
        item.callTimestamp ? (
          <span className="whitespace-nowrap text-foreground">
            {formatDate(item.callTimestamp, orgTimezone, 'MMM D, h:mm A z')}
          </span>
        ) : (
          '-'
        ),
    },
    {
      header: 'Duration',
      key: 'corrected_duration',
      sortable: true,
      width: '120px',
      cell: (item: Call) => {
        const seconds = item.corrected_duration;
        const minutes = item.call_length;
        if (seconds != null && typeof seconds === 'number' && seconds > 0) {
          return (
            <span className="text-foreground tabular-nums">
              {formatDurationFromSeconds(seconds)}
            </span>
          );
        }
        if (!minutes || typeof minutes !== 'number' || minutes <= 0) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <span className="text-foreground tabular-nums">
            {formatCallDuration(minutes, 'minutes')}
          </span>
        );
      },
    },
    {
      header: 'Answered By',
      key: 'answered_by',
      sortable: true,
      width: '140px',
      cell: (item: Call) => {
        const answeredBy = item.answered_by || 'no answer';
        const style = getAnsweredByStyle(answeredBy);
        if (!style) return null;
        return (
          <Badge className={`inline-flex items-center gap-1.5 ${style.background} ${style.text}`}>
            {answeredBy === 'human' ? (
              <>
                <User className="w-3 h-3" />
                <span>Human</span>
              </>
            ) : answeredBy === 'voicemail' ? (
              <>
                <Voicemail className="w-3 h-3" />
                <span>Voicemail</span>
              </>
            ) : (
              <>
                <XCircle className="w-3 h-3" />
                <span>No Answer</span>
              </>
            )}
          </Badge>
        );
      },
    },
    {
      header: 'Flow',
      key: 'flowName',
      width: '180px',
      cell: (item: Call) => {
        // Keyed off the call's direction, not off a missing flow: an outbound
        // call whose flow failed to resolve must stay a dash (SPEC-2383).
        if (item.inbound) return <span className="text-foreground">Inbound</span>;
        const flowName = item.flowName || item.flow_name;
        if (!flowName) return <span className="text-muted-foreground">-</span>;
        return (
          <div className="truncate text-foreground" title={flowName}>
            {flowName}
          </div>
        );
      },
    },
    {
      header: 'Flow Type',
      key: 'flowType',
      width: '120px',
      cell: (item: Call) => {
        const flowType = item.flowType || '';
        if (!flowType) return <span className="text-muted-foreground">-</span>;
        const style = getFlowTypeStyle(flowType);
        return (
          <Badge className={`inline-flex items-center ${style.background} ${style.text}`}>
            {flowType}
          </Badge>
        );
      },
    },
    {
      header: 'Outcome',
      key: 'call_analysis',
      width: '150px',
      cell: (item: Call) => {
        const outcome = item.call_analysis?.outcome;
        if (!outcome) return <span className="text-muted-foreground">-</span>;
        return <OutcomeBadge outcome={outcome} />;
      },
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="full" className="mx-0 h-full">
        <Modal.Header>
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
              <Typography variant="h2">
                {type === 'answered'
                  ? 'Conversations'
                  : type === 'opportunities'
                    ? 'Opportunities'
                    : 'Calls Made'}
              </Typography>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
                <Calendar className="h-3.5 w-3.5" />
                <span>
                  {dateRange.from && dateRange.to
                    ? `${formatDate(dateRange.from, orgTimezone, 'MMM D, YYYY')} - ${formatDate(dateRange.to, orgTimezone, 'MMM D, YYYY')}`
                    : 'All Calls'}
                </span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {type === 'answered'
                ? `${formatNumber(conversations)} ${conversations === 1 ? 'record' : 'records'}`
                : type === 'opportunities'
                  ? `${formatNumber(opportunities)} ${opportunities === 1 ? 'record' : 'records'}`
                  : `${formatNumber(callsMade)} ${callsMade === 1 ? 'record' : 'records'}`}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="flex-1 flex flex-col min-h-0">
          <div className="flex flex-1 min-h-0 gap-4 overflow-hidden md:flex-row flex-col">
            <div
              className={`md:w-[65%] w-full flex-1 md:flex-none flex flex-col min-h-0 ${showInfoOnMobile ? 'hidden md:flex' : ''}`}
            >
              <div className="flex flex-col flex-1 min-h-0 [&_tbody_tr]:cursor-pointer">
                <div className="flex w-full mb-4 shrink-0">
                  <CallStatsHeader
                    onSearch={setSearchQuery}
                    onFilterByAnsweredBy={setSelectedAnsweredBy}
                    onFilterByFlows={setSelectedFlowIds}
                    onFilterByFlowTypes={setSelectedFlowTypes}
                    onDateRangeChange={(range) => {
                      const { dateRange: normalized } = normalizeDateRange(
                        range,
                        organization?.timezone
                      );
                      setModalDateRange(normalized);
                    }}
                    onReset={() => {
                      setSearchQuery('');
                      setSelectedAnsweredBy(null);
                      setSelectedFlowIds([]);
                      setSelectedFlowTypes([]);
                      setModalDateRange(dateRange);
                    }}
                    searchQuery={searchQuery}
                    selectedAnsweredBy={selectedAnsweredBy}
                    selectedFlowIds={selectedFlowIds}
                    selectedFlowTypes={selectedFlowTypes}
                    dateRange={modalDateRange}
                    flowOptions={flowFilterOptions}
                    flowTypeOptions={flowTypeOptions}
                    isFlowOptionsLoading={isFlowOptionsLoading}
                  />
                </div>
                <TableCard
                  className={cn(STAT_CELL_TEXT, 'flex-1 min-h-[240px]')}
                  footer={
                    <OffsetPagination
                      currentPage={gqlCurrentPage}
                      totalItems={gqlTotal ?? 0}
                      pageSize={gqlPageSize}
                      isLoading={gqlIsLoading ?? false}
                      label="calls"
                      onNextPage={() => {
                        const next = gqlCurrentPage + 1;
                        setGqlCurrentPage(next);
                        gqlLoadPage?.(next * gqlPageSize, gqlPageSize);
                      }}
                      onPrevPage={() => {
                        const prev = gqlCurrentPage - 1;
                        setGqlCurrentPage(prev);
                        gqlLoadPage?.(prev * gqlPageSize, gqlPageSize);
                      }}
                      onPageChange={(page) => {
                        setGqlCurrentPage(page);
                        gqlLoadPage?.(page * gqlPageSize, gqlPageSize);
                      }}
                      onPageSizeChange={(size) => {
                        setGqlPageSize(size);
                        setGqlCurrentPage(0);
                        gqlLoadPage?.(0, size);
                      }}
                    />
                  }
                >
                  <Table
                    data={filteredCalls}
                    columns={columns}
                    sortable={true}
                    onSort={handleSort}
                    keyExtractor={(item) => item.call_id as string}
                    loading={gqlIsLoading}
                    emptyStateText="No calls match your filters"
                    rowSelectable
                    selectedRowId={selectedCall?.call_id}
                    onRowSelect={(id) => {
                      const c = filteredCalls.find((call) => call.call_id === id);
                      if (c) {
                        setSelectedCall(c);
                        setShowInfoOnMobile(true);
                      }
                    }}
                    className="w-full"
                    size="xl"
                  />
                </TableCard>
              </div>
            </div>
            <div
              className={`md:w-[35%] w-full rounded-xl border border-border bg-muted/30 overflow-y-auto scrollbar-custom ${!showInfoOnMobile && !selectedCall ? 'hidden md:block' : ''}`}
            >
              {showInfoOnMobile && (
                <div className="p-4 border-b border-border md:hidden">
                  <BackButton
                    onClick={() => {
                      setShowInfoOnMobile(false);
                      setSelectedCall(null);
                    }}
                  />
                </div>
              )}
              {selectedCall ? (
                <div className="flex min-h-0 flex-col">
                  {showCrmStage && (
                    <div className="border-b border-border px-4 py-3">
                      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        CRM Stage
                      </div>
                      <CrmStageDisplay stages={selectedCall.crmStages} />
                    </div>
                  )}
                  <CallDetailsPanel
                    call={selectedCall}
                    orgTimezone={orgTimezone}
                    flowInfo={
                      selectedCall.flowName
                        ? {
                            name: selectedCall.flowName as string,
                            type: (selectedCall.flowType as string | undefined) || '-',
                          }
                        : undefined
                    }
                    contact={selectedCall.contact}
                    contactFirstName={selectedCall.contact?.firstName}
                    displayName={selectedCall.fullName as string | undefined}
                    displayPhone={selectCallDisplayNumber(selectedCall) || undefined}
                    assistantName={organization?.assistant_name}
                    onEditContact={
                      selectedCall.contactId
                        ? () => handleOpenEditModal({ id: selectedCall.contactId } as Contact)
                        : undefined
                    }
                    onEditOutcome={() =>
                      setEditOutcomeCallId(selectedCall.call_id || selectedCall.id)
                    }
                  />
                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Phone className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Select a call to view details
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click any row in the table to see the full call breakdown
                  </p>
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <EditContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setContactToEdit(null);
        }}
        onSubmit={async () => {}}
        onSuccess={(data) => {
          if (selectedCall && selectedCall.contactId === contactToEdit?.id) {
            setSelectedCall({
              ...selectedCall,
              fullName: `${data.firstName || ''} ${data.lastName || ''}`.trim() || '-',
            } as Call);
          }
        }}
        contact={contactToEdit}
        dateRange={dateRange}
      />
      {editOutcomeCallId && (
        <EditCallAnalysisModal
          isOpen={!!editOutcomeCallId}
          onClose={() => setEditOutcomeCallId(null)}
          callId={editOutcomeCallId}
          currentOutcome={selectedCall?.call_analysis?.outcome || ''}
          onSuccess={(newOutcome) => {
            if (selectedCall) {
              setSelectedCall({
                ...selectedCall,
                call_analysis: { ...selectedCall.call_analysis, outcome: newOutcome },
              } as Call);
            }
          }}
        />
      )}
    </>
  );
};
